/** Live-query subscription engine (extracted from RethinkClient.onSnapshot). */

import { compareDocs, findInsertionIndex } from './connection';
import type { DocumentData, Protocol } from './protocol';
import {
  CollectionReference,
  DocumentReference,
  Query,
  RethinkQueryDocumentSnapshot,
  type DocumentChange,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
} from './references';

/**
 * In-flight bootstrap fetches keyed by `collection|options` fingerprint. When
 * two live subscriptions share the same query, only one initial REST fetch is
 * issued; the rest await the same promise. The fingerprint matches the socket
 * subscription key, so this exactly dedupes what the server already coalesces.
 */
const bootstrapInFlight = new Map<string, Promise<unknown>>();

/** The RethinkClient capabilities a live subscription needs. */
export interface SnapshotHost {
  getDocs<T>(q: Query<T> | CollectionReference<T>): Promise<QuerySnapshot<T>>;
  onIdentityChange(listener: () => void): () => void;
  subscribe(
    table: string,
    options: Protocol.QueryOptions | null,
    isGroup: boolean,
    callback: (change: Protocol.ChangeEvent) => void,
  ): () => void;
  /** Whether the last bootstrap carried the Firebase ID token. */
  hasIdentity(): boolean;
}

export function watchQuery<T = DocumentData>(
  host: SnapshotHost,
  q: Query<T> | CollectionReference<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: Error) => void,
) {
  const queryObj = q instanceof CollectionReference ? new Query<T>(q) : q;
  const options = queryObj.buildOptions();
  const colName = queryObj.colRef.name;

  let currentDocs: T[] = [];
  let isInitial = true;
  let loadSucceeded = false;
  // Whether the last successful bootstrap carried the Firebase ID token.
  // An anonymous first attempt can resolve with an empty snapshot (no
  // error), which would otherwise stick forever — see refetchOnIdentity.
  let bootstrappedAuthed = false;
  let pendingChanges: DocumentChange<T>[] = [];
  let scheduleTimeoutId: any = null;

  // Wire docs always carry string ids; T is the payload shape the adapter
  // cannot prove — one boundary cast reused for every id read below.
  const idOf = (d: T): string => (d as unknown as { id: string }).id;

  const flushBatch = () => {
    if (pendingChanges.length === 0) return;

    const deduplicatedChangesMap = new Map<string, DocumentChange<T>>();
    pendingChanges.forEach((change) => {
      deduplicatedChangesMap.set(change.doc.id, change);
    });

    const consolidatedChanges = Array.from(deduplicatedChangesMap.values());
    const docsList = currentDocs.map((d) => {
      const id = idOf(d);
      const docRef = new DocumentReference<T>(queryObj.colRef.client, queryObj.colRef.name, id);
      return new RethinkQueryDocumentSnapshot<T>(id, d, docRef);
    });

    onNext({
      docs: docsList,
      docChanges: () => consolidatedChanges,
      empty: docsList.length === 0, // --- FIXED: Provided natively ---
      forEach(callback: (doc: QueryDocumentSnapshot<T>) => void, thisArg?: unknown) {
        docsList.forEach(callback, thisArg);
      },
    });

    pendingChanges = [];
    scheduleTimeoutId = null;
  };

  const unsubscribe = host.subscribe(colName, options, queryObj.isGroup, (change) => {
    const { type, old_val, new_val } = change;
    const docId = new_val?.id || old_val?.id;
    const oldIndex = currentDocs.findIndex((d) => idOf(d) === docId);

    let docChange: DocumentChange<T> | null = null;
    const docRef = new DocumentReference<T>(queryObj.colRef.client, queryObj.colRef.name, docId);

    if (type === 'add' || (type === 'change' && oldIndex === -1)) {
      const insertIndex = findInsertionIndex(currentDocs, new_val, options.orderBy);
      currentDocs.splice(insertIndex, 0, new_val);

      docChange = {
        type: 'added',
        doc: new RethinkQueryDocumentSnapshot<T>(docId, new_val, docRef),
        oldIndex: -1,
        newIndex: insertIndex,
      };
    } else if (type === 'remove' || (type === 'change' && new_val === null)) {
      if (oldIndex !== -1) {
        const removedDoc = currentDocs.splice(oldIndex, 1)[0];
        docChange = {
          type: 'removed',
          doc: new RethinkQueryDocumentSnapshot<T>(docId, removedDoc, docRef),
          oldIndex,
          newIndex: -1,
        };
      }
    } else if (type === 'change') {
      if (oldIndex !== -1) {
        currentDocs.splice(oldIndex, 1);
        const newIndex = findInsertionIndex(currentDocs, new_val, options.orderBy);
        currentDocs.splice(newIndex, 0, new_val);

        docChange = {
          type: 'modified',
          doc: new RethinkQueryDocumentSnapshot<T>(docId, new_val, docRef),
          oldIndex,
          newIndex,
        };
      }
    }

    if (!isInitial && docChange) {
      pendingChanges.push(docChange);

      if (!scheduleTimeoutId) {
        const scheduler =
          typeof requestAnimationFrame !== 'undefined'
            ? requestAnimationFrame
            : (cb: any) => setTimeout(cb, 0);

        scheduleTimeoutId = scheduler(flushBatch);
      }
    }
  });

  const loadInitial = () => {
    const bootstrapKey = `${queryObj.isGroup ? 'group:' : ''}${colName}|${JSON.stringify(options)}`;
    let bootstrap = bootstrapInFlight.get(bootstrapKey) as Promise<QuerySnapshot<T>> | undefined;
    if (!bootstrap) {
      bootstrap = host.getDocs<T>(queryObj);
      bootstrapInFlight.set(bootstrapKey, bootstrap);
      // Release the slot when the shared fetch settles (resolve or reject).
      bootstrap.then(
        () => {
          if (bootstrapInFlight.get(bootstrapKey) === bootstrap)
            bootstrapInFlight.delete(bootstrapKey);
        },
        () => {
          if (bootstrapInFlight.get(bootstrapKey) === bootstrap)
            bootstrapInFlight.delete(bootstrapKey);
        },
      );
    }
    const withIdentity = host.hasIdentity();
    return bootstrap
      .then((snapshot: QuerySnapshot<T>) => {
        loadSucceeded = true;
        bootstrappedAuthed = withIdentity;
        currentDocs = snapshot.docs.map((d) => d.data());
        currentDocs.sort((a, b) => compareDocs(a, b, options.orderBy));
        isInitial = false;

        const docsList = currentDocs.map((d) => {
          const id = idOf(d);
          const docRef = new DocumentReference<T>(queryObj.colRef.client, queryObj.colRef.name, id);
          return new RethinkQueryDocumentSnapshot<T>(id, d, docRef);
        });

        onNext({
          docs: docsList,
          docChanges: () =>
            snapshot.docs.map((d, i) => ({
              type: 'added',
              doc: d,
              oldIndex: -1,
              newIndex: i,
            })),
          empty: docsList.length === 0, // --- FIXED: Provided natively ---
          forEach(callback: (doc: QueryDocumentSnapshot<T>) => void, thisArg?: unknown) {
            docsList.forEach(callback, thisArg);
          },
        });
      })
      .catch((err) => {
        if (onError) onError(err);
      });
  };

  loadInitial();

  // If the initial fetch fired before the Firebase ID token was ready (e.g.
  // right after login or a page refresh), it either failed with 403 or —
  // worse — succeeded anonymously with an empty snapshot. Either way the
  // snapshot is untrustworthy, so refetch once the identity becomes
  // available. Skipped only when a previous bootstrap already ran WITH the
  // token (avoids duplicate fetches on periodic token refreshes).
  const refetchOnIdentity = () => {
    if (!loadSucceeded || !bootstrappedAuthed) void loadInitial();
  };
  const stopListeningIdentity = host.onIdentityChange(refetchOnIdentity);

  return () => {
    stopListeningIdentity();
    if (scheduleTimeoutId) {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(scheduleTimeoutId);
      } else {
        clearTimeout(scheduleTimeoutId);
      }
    }
    unsubscribe();
  };
}
